import { useState, useEffect, useCallback, useRef } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { EsporaiInput, EsporaiPhoneInput, OnboardingStepLayout } from 'components';
import { COUNTRIES } from 'constants';

const OnboardingNameAndPhoneStep = ({
  nameValue,
  phoneValue,
  prefixValue,
  onNameChange,
  onPhoneChange,
  onPrefixChange,
  onValidation,
}) => {
  const [localName, setLocalName] = useState(nameValue || '');
  const [phone, setPhone] = useState('');
  const [prefix, setPrefix] = useState('+598');
  const [nameError, setNameError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [nameFocused, setNameFocused] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [hasTypedName, setHasTypedName] = useState(Boolean(nameValue));
  const validityRef = useRef();
  const isUserEditingPhoneRef = useRef(false);

  // Sincronizar desde el padre solo ante cambios externos (carga inicial/reset),
  // no en cada tecleo local para evitar "rebobinar" el input durante estados inválidos transitorios.
  useEffect(() => {
    const localParsed = phone ? parsePhoneNumberFromString(prefix + phone) : null;
    const localE164 = !phone ? '' : localParsed?.isValid?.() ? localParsed.format('E.164') : null;

    // Mientras el usuario está editando (número o prefijo), no rehidratar desde el padre.
    // Solo liberar el lock cuando el padre refleje exactamente lo que representa el estado local.
    if (isUserEditingPhoneRef.current) {
      const parentMatchesLocal =
        (localE164 !== null && phoneValue === localE164) || (!phone && !phoneValue);
      if (!parentMatchesLocal) {
        return;
      }
      isUserEditingPhoneRef.current = false;
    }

    if (phoneValue?.startsWith('+')) {
      const parsed = parsePhoneNumberFromString(phoneValue);
      if (parsed?.isValid?.()) {
        const countryCode = parsed.country?.toLowerCase?.();
        const countryEntry = COUNTRIES.find(c => c.code.toLowerCase() === countryCode);
        const prefixToSet = countryEntry?.prefix ?? `+${parsed.countryCallingCode}`;
        setPrefix(prefixToSet);
        // Argentina usa +549 en el dropdown (el 9 ya está en el prefijo); libphonenumber devuelve nationalNumber "91158871347". No duplicar el 9 al mostrar.
        const displayPhone =
          countryCode === 'ar' && prefixToSet === '+549' && parsed.nationalNumber.startsWith('9')
            ? parsed.nationalNumber.slice(1)
            : parsed.nationalNumber;
        if (prefix !== prefixToSet) setPrefix(prefixToSet);
        if (phone !== displayPhone) setPhone(displayPhone);
        return;
      }
      const fallbackPrefix = prefixValue || '+598';
      if (prefix !== fallbackPrefix) setPrefix(fallbackPrefix);
      return;
    }
    const fallbackPrefix = prefixValue || '+598';
    const fallbackPhone = phoneValue || '';
    if (prefix !== fallbackPrefix) setPrefix(fallbackPrefix);
    if (phone !== fallbackPhone) setPhone(fallbackPhone);
  }, [phoneValue, prefixValue, phone, prefix]);

  useEffect(() => {
    setLocalName(nameValue || '');
  }, [nameValue]);

  const validateName = useCallback(() => {
    if (!localName || !localName.trim()) {
      setNameError('El nombre es requerido');
      return false;
    }
    if (localName.trim().length < 2) {
      setNameError('El nombre debe tener al menos 2 caracteres');
      return false;
    }
    const nameRegex = /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s'-]+$/;
    if (!nameRegex.test(localName.trim())) {
      setNameError('El nombre solo debe contener letras');
      return false;
    }
    setNameError(null);
    return true;
  }, [localName]);

  const validatePhone = useCallback(() => {
    if (!phone) {
      setPhoneError('El número de WhatsApp es requerido');
      return false;
    }
    const full = prefix + phone;
    const phoneNumber = parsePhoneNumberFromString(full);
    if (!phoneNumber || !phoneNumber.isValid()) {
      setPhoneError('Número de teléfono inválido');
      return false;
    }
    setPhoneError(null);
    return true;
  }, [phone, prefix]);

  useEffect(() => {
    onNameChange?.(localName);
  }, [localName, onNameChange]);

  const validateAndEmitPhone = useCallback(() => {
    if (!phone) {
      onPhoneChange?.('');
      return;
    }
    const full = prefix + phone;
    const phoneNumber = parsePhoneNumberFromString(full);
    if (!phoneNumber?.isValid()) {
      // No emitir '' cuando el usuario ya escribió dígitos: evita que se borre el campo al completar el número.
      return;
    }
    // Solo emitir si el país del número coincide con el prefijo elegido; evita que +541 se emita como USA y cambie el prefijo.
    const parsedPrefix = `+${phoneNumber.countryCallingCode}`;
    const prefixMatchesCountry = prefix === parsedPrefix || prefix.startsWith(parsedPrefix);
    if (!prefixMatchesCountry) {
      return;
    }
    onPhoneChange?.(phoneNumber.format('E.164'));
  }, [phone, prefix, onPhoneChange]);

  useEffect(() => {
    validateAndEmitPhone();
  }, [phone, prefix, validateAndEmitPhone]);

  useEffect(() => {
    const isNameValid = validateName();
    const isPhoneValid = validatePhone();
    const isValid = isNameValid && isPhoneValid;
    if (validityRef.current !== isValid) {
      onValidation?.(isValid);
      validityRef.current = isValid;
    }
  }, [localName, phone, prefix, validateName, validatePhone, onValidation]);

  const displayNameError = nameFocused && hasTypedName ? nameError : null;
  const displayPhoneError = phoneFocused ? phoneError : null;

  return (
    <OnboardingStepLayout
      title="Contanos sobre vos"
      subtitle="Ingresá tu nombre y tu WhatsApp para que podamos comunicarnos."
    >
      <div className="space-y-4">
        <EsporaiInput
          autoFocus
          id="onboarding-first-name"
          name="first_name"
          type="text"
          value={localName}
          onChange={(val) => {
            setLocalName(val);
            if (!hasTypedName && val) setHasTypedName(true);
          }}
          placeholder="Tu nombre"
          error={displayNameError}
          onFocus={() => setNameFocused(true)}
          onBlur={() => setNameFocused(false)}
        />
        <EsporaiPhoneInput
          id="onboarding-phone-number"
          name="phone_number"
          value={phone}
          prefix={prefix}
          onChange={(val) => {
            isUserEditingPhoneRef.current = true;
            setPhone(val);
          }}
          onChangePrefix={(newPrefix) => {
            isUserEditingPhoneRef.current = true;
            setPrefix(newPrefix);
            onPrefixChange?.(newPrefix);
          }}
          placeholder="Ingresá tu WhatsApp"
          error={displayPhoneError}
          onFocus={() => setPhoneFocused(true)}
          onBlur={() => setPhoneFocused(false)}
        />
      </div>
    </OnboardingStepLayout>
  );
};

export default OnboardingNameAndPhoneStep;
